import type { TacticalBoard, TacticalFeature } from "@/services/tactical.service";
import {
  buildBoardPackage,
  downloadJson,
  featureToCreatePayload,
  parseBoardImport,
  readJsonFile,
  type FeatureCreatePayload,
} from "@/tactical/io/boardPackage";
import { featuresForGisExport, normalizeToSimpleFeatures } from "@/tactical/io/gisNormalize";
import {
  downloadKml,
  featureCollectionToKml,
  parseKmlText,
  parseKmzArrayBuffer,
} from "@/tactical/io/kmlIo";
import {
  downloadBlob,
  featureCollectionToShapefileZip,
  parseShapefileBuffer,
  parseShapefileFiles,
} from "@/tactical/io/shapefileIo";

export type BoardExportFormat = "json" | "kml" | "shapefile";

export type GisImportPrepared = {
  payloads: FeatureCreatePayload[];
  skipped: number;
  sourceLabel: string;
};

function safeBoardFilename(
  board: Pick<TacticalBoard, "id" | "name"> | null | undefined,
  boardId: number | null,
): string {
  return (
    (board?.name || `board-${boardId ?? "export"}`)
      .replace(/[^\w\-а-яА-ЯёЁ]+/gi, "_")
      .slice(0, 64) || "board"
  );
}

function featuresToPayloads(features: GeoJSON.Feature[]): {
  payloads: FeatureCreatePayload[];
  skipped: number;
} {
  const payloads: FeatureCreatePayload[] = [];
  let skipped = 0;
  for (const f of features) {
    const p = featureToCreatePayload(f);
    if (p) payloads.push(p);
    else skipped += 1;
  }
  return { payloads, skipped };
}

export async function exportBoardAs(
  format: BoardExportFormat,
  board: Pick<TacticalBoard, "id" | "name" | "kind"> | null,
  features: TacticalFeature[],
  boardId: number | null,
): Promise<{ count: number }> {
  const base = safeBoardFilename(board, boardId);

  if (format === "json") {
    const pkg = buildBoardPackage(board, features);
    downloadJson(`${base}.tactical.json`, pkg);
    return { count: pkg.featureCollection.features.length };
  }

  const fc = featuresForGisExport(features as GeoJSON.Feature[]);
  if (fc.features.length === 0) {
    throw new Error("Нет поддерживаемых геометрий для экспорта");
  }

  if (format === "kml") {
    const kml = await featureCollectionToKml(fc);
    downloadKml(`${base}.kml`, kml);
    return { count: fc.features.length };
  }

  const zip = await featureCollectionToShapefileZip(fc, base);
  downloadBlob(`${base}.shp.zip`, zip);
  return { count: fc.features.length };
}

function detectImportKind(file: File): "json" | "kml" | "kmz" | "shapefile-zip" | "shapefile-set" {
  const name = file.name.toLowerCase();
  if (name.endsWith(".json")) return "json";
  if (name.endsWith(".kml")) return "kml";
  if (name.endsWith(".kmz")) return "kmz";
  if (name.endsWith(".zip")) return "shapefile-zip";
  if (/\.(shp|dbf|prj|shx)$/i.test(name)) return "shapefile-set";
  // content sniff for json without extension rare — default json attempt
  if (file.type.includes("json")) return "json";
  if (file.type.includes("kml") || file.type.includes("xml")) return "kml";
  throw new Error("Неизвестный формат. Ожидается JSON, KML/KMZ или Shapefile (ZIP)");
}

/** Один файл: JSON / KML / KMZ / SHP ZIP. */
export async function parseGisImportFile(file: File): Promise<GisImportPrepared> {
  const kind = detectImportKind(file);

  if (kind === "json") {
    const raw = await readJsonFile(file);
    const parsed = parseBoardImport(raw);
    const norm = normalizeToSimpleFeatures({
      type: "FeatureCollection",
      features: parsed.features,
    });
    const { payloads, skipped } = featuresToPayloads(norm.features);
    return {
      payloads,
      skipped: skipped + norm.skipped,
      sourceLabel: parsed.boardName ? `JSON «${parsed.boardName}»` : "JSON",
    };
  }

  if (kind === "kml") {
    const text = await file.text();
    const { features, skipped } = await parseKmlText(text);
    const prep = featuresToPayloads(features);
    return {
      payloads: prep.payloads,
      skipped: skipped + prep.skipped,
      sourceLabel: "KML",
    };
  }

  if (kind === "kmz") {
    const { features, skipped } = await parseKmzArrayBuffer(await file.arrayBuffer());
    const prep = featuresToPayloads(features);
    return {
      payloads: prep.payloads,
      skipped: skipped + prep.skipped,
      sourceLabel: "KMZ",
    };
  }

  // ZIP: try shapefile; if fails and looks like kmz already handled
  const { features, skipped } = await parseShapefileBuffer(await file.arrayBuffer());
  const prep = featuresToPayloads(features);
  return {
    payloads: prep.payloads,
    skipped: skipped + prep.skipped,
    sourceLabel: "Shapefile ZIP",
  };
}

/** Несколько sidecar-файлов shapefile. */
export async function parseGisImportFiles(files: File[]): Promise<GisImportPrepared> {
  if (files.length === 1) return parseGisImportFile(files[0]!);
  const { features, skipped } = await parseShapefileFiles(files);
  const prep = featuresToPayloads(features);
  return {
    payloads: prep.payloads,
    skipped: skipped + prep.skipped,
    sourceLabel: "Shapefile",
  };
}
